import React from 'react';

/**
 * Render inline markdown spans within a line of agent text: **bold**, *italic*,
 * and `code`. A hand-rolled renderer (no markdown dependency) that builds real
 * React elements — never raw HTML — so model output can't inject markup. Bold
 * is matched before italic so `**x**` is not misread as two italic markers.
 */
function renderInline(text, keyPrefix) {
  const parts = String(text).split(/(\*\*[^*]+\*\*|\*[^*\n]+\*|`[^`\n]+`)/g).filter(Boolean);
  return parts.map((part, i) => {
    const key = `${keyPrefix}-${i}`;
    let m = part.match(/^\*\*([^*]+)\*\*$/);
    if (m) return <strong key={key}>{m[1]}</strong>;
    m = part.match(/^\*([^*\n]+)\*$/);
    if (m) return <em key={key}>{m[1]}</em>;
    m = part.match(/^`([^`\n]+)`$/);
    if (m) return <code key={key} className="ai-rich-code">{m[1]}</code>;
    return <React.Fragment key={key}>{part}</React.Fragment>;
  });
}

/**
 * Render agent text as blocks: blank-line-separated paragraphs, with runs of
 * "- "/"* " lines collapsed into a bulleted list, runs of "1." lines into a
 * numbered list, and "#"/"##"/"###" lines as headings. Fixes agent output that
 * previously printed literal markdown (headings, numbered lists) as plain text.
 *
 * @param {string} text
 * @returns {JSX.Element}
 */
export function renderRichText(text) {
  const lines = String(text).replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let para = [];
  let list = [];      // unordered items
  let ordered = [];   // ordered items

  const flushPara = () => { if (para.length) { blocks.push({ type: 'p', lines: para }); para = []; } };
  const flushList = () => { if (list.length) { blocks.push({ type: 'ul', items: list }); list = []; } };
  const flushOrdered = () => { if (ordered.length) { blocks.push({ type: 'ol', items: ordered }); ordered = []; } };
  const flushAll = () => { flushPara(); flushList(); flushOrdered(); };

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flushAll(); continue; }

    const heading = line.match(/^(#{1,3})\s+(.*)$/);
    if (heading) { flushAll(); blocks.push({ type: 'h', level: heading[1].length, text: heading[2] }); continue; }

    // Numbered list: "1." / "2)" etc. Checked before the bullet rule.
    const numbered = line.match(/^\d+[.)]\s+(.*)$/);
    if (numbered) { flushPara(); flushList(); ordered.push(numbered[1]); continue; }

    const bullet = line.match(/^[-*]\s+(.*)$/);
    if (bullet) { flushPara(); flushOrdered(); list.push(bullet[1]); continue; }

    flushList(); flushOrdered(); para.push(line);
  }
  flushAll();

  return (
    <>
      {blocks.map((b, i) => {
        if (b.type === 'ul') {
          return (
            <ul className="ai-rich-list" key={i}>
              {b.items.map((it, j) => <li key={j}>{renderInline(it, `${i}-${j}`)}</li>)}
            </ul>
          );
        }
        if (b.type === 'ol') {
          return (
            <ol className="ai-rich-list" key={i}>
              {b.items.map((it, j) => <li key={j}>{renderInline(it, `${i}-${j}`)}</li>)}
            </ol>
          );
        }
        if (b.type === 'h') {
          return (
            <p className={`ai-rich-heading ai-rich-h${b.level}`} key={i}>
              {renderInline(b.text, `${i}-h`)}
            </p>
          );
        }
        return (
          <p className="ai-rich-text" key={i}>
            {b.lines.map((ln, j) => (
              <React.Fragment key={j}>
                {renderInline(ln, `${i}-${j}`)}
                {j < b.lines.length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </>
  );
}

export default renderRichText;
